<template>
  <BasePayment
    :loading="loading"
    :textHeader="$t('apps.payment.label.payservicepackage')"
    :steps="progress.steps"
    :active="progress.active"
    :showBackButton="isShowBackButton"
  >
    <PaymentFail
      v-if="isPaymentFailed"
      @next="() => this.$router.push({ name: 'Home' })"
    />
    <template
      v-else
    >
      <List
        v-if="isStepOne"
        :detail="detail"
        :totalNumber="totalNumber"
        @next="onListClickedNext"
      />
      <Confirm
        v-else-if="isStepTwo"
        :detail="detail"
        :totalNumber="totalNumber"
        :isSubmitted="tabConfirm.isSubmitted"
        :paymentMethod="paymentMethod"
        @prev="onConfirmClickedPrev"
        @next="onConfirmClickedNext"
      />
      <Paid
        v-else-if="isStepThree"
        :transaction="transaction"
        @next="onPaidClickedNext"
      />
    </template>
    <BaseModalScanPayment
      :id="MODAL_SCAN_PAYMENT"
      :qrCodeUri="qrCodeUri"
    />
  </BasePayment>
</template>

<script>
import axios from 'axios'
import { get } from 'lodash'
import { mapState, mapActions } from 'vuex'
import LocalStorage from 'store'
import project from '@/project'
import StepProgress from '@/components/custom/StepProgress.vue'
import List from './InvoiceList.vue'
import Confirm from './InvoiceConfirm.vue'
import Paid from './Paid.vue'
import BaseModalScanPayment from '@/components/base/BaseModalScanPayment.vue'
import PaymentFail from './PaymentFail.vue'
import { BILLING_INVOICE_STATUS, BILLING_PAYMENT_METHOD } from '../../helpers/enum'

export default {
  components: {
    StepProgress,
    List,
    Confirm,
    Paid,
    PaymentFail,
    BaseModalScanPayment,
  },

  data () {
    return {
      MODAL_SCAN_PAYMENT: 'MODAL_SCAN_PAYMENT',
      loading: false,
      progress: {
        steps: [
          this.$t('apps.payment.message.progressstep1'),
          this.$t('apps.payment.message.progressstep2'),
          this.$t('apps.payment.message.progressstep3'),
        ],
        active: 0,
      },
      query: {
        id: this.$route.query.id,
        router: this.$route.query.router,
      },
      tabConfirm: {
        isSubmitted: false,
      },
      qrCodeUri: null,
      timer: null,
      isScanPromptPayDone: false,
      isPaymentFailed: false,
    }
  },

  computed: {
    ...mapState('billing', {
      paymentMethod: 'paymentMethod',
      transaction: 'transactionSingle',
      purchasePackage: 'purchasePackage',
    }),
    isStepOne () {
      return this.progress.active === 0
    },
    isStepTwo () {
      return this.progress.active === 1
    },
    isStepThree () {
      return this.progress.active === 3
    },
    isShowBackButton () {
      return this.isStepOne || this.isStepTwo
    },
    totalNumber() {
      return this.purchasePackage.total
    },
    packageKey() {
      return this.purchasePackage.key
    },
    monthNumber() {
      return this.purchasePackage.month
    },
    detail() {
      return this.purchasePackage.detail
    },
  },

  mounted () {
    this.setState()
    if (!this.packageKey) {
      this.$router.push({
        name: 'Home',
      })
    }
  },

  methods: {
    ...mapActions('billing', {
      getMasterData: 'getMasterData',
      updatePaymentMethod: 'updatePaymentMethod',
      chargePackage: 'chargePackage',
      getTransactionBy: 'getTransactionBy',
    }),
    setState () {
      this.$store.dispatch('setSection', 'login')
      this.setTabTitle(this.$t('apps.payment.label.payservicepackage'))
    },
    async fetchPaymentMethod () {
      this.loading = true
      await this.getMasterData()
      this.loading = false
      this.showFinish(this.$Progress)
    },
    onListClickedNext () {
      this.progress.active = 1
      this.fetchPaymentMethod()
    },
    onConfirmClickedPrev () {
      this.progress.active = 0
    },
    async payWithCard(form) {
      this.tabConfirm.isSubmitted = true

      const dataSetPaymentMethod = {
        'card': {
          'card_number': form.creditCard,
          'cvv_number': form.creditCardCVV,
          'expired_date_month': form.creditCardExpireDateMonth,
          'expired_date_year': form.creditCardExpireDateYear,
          'holder_name': form.fullName,
        },
        'city': form.city,
        'address1': form.address1,
        'address2': form.address2,
        'zipcode': form.zipcode,
        'country_code': form.countryCode,
        'phone_number': form.phoneNumber,
      }
      const responseUpdatePaymentMethod = await this.updatePaymentMethod({
        type: form.paymentMethod,
        data: dataSetPaymentMethod,
      })

      if (!responseUpdatePaymentMethod.success) {
        this.notify(this.$vs, {
          text: this.$t('message.somethingwentwrong'),
          color: 'warning',
          icon: 'warning',
        })
        this.tabConfirm.isSubmitted = false
      } else {
        const responseToken = await axios.post(project.config.web.omise_vault_api, {
          card: {
            name: form.fullName,
            city: form.city,
            postal_code: form.zipcode,
            number: form.creditCard,
            security_code: form.creditCardCVV,
            expiration_month: form.creditCardExpireDateMonth,
            expiration_year: form.creditCardExpireDateYear,
          },
        }, {
          auth: {
            username: project.config.web.omise_public_key,
          },
        })

        const responseChargePackage = await this.chargePackage({
          'package': this.packageKey,
          'month': this.monthNumber,
          'detail': this.detail,
          'token': responseToken.data.id,
          'redirect_uri': `${project.config.web.app}desktop/payment/package/done`,
        })
        const chargeId = get(responseChargePackage, 'data.charge.id')
        const isPaid = get(responseChargePackage, 'data.charge.paid')
        const authUri = get(responseChargePackage, 'data.charge.authorize_uri')
        if (!responseChargePackage.success || !chargeId) {
          this.notify(this.$vs, {
            text: this.$t('apps.payment.message.paymentfailed'),
            color: 'warning',
            icon: 'warning',
          })
          this.tabConfirm.isSubmitted = false
        } else if (authUri) {
          LocalStorage.set('chargeId', chargeId)
          window.location = authUri
        } else if (isPaid) {
          await this.getTransactionBy({ chargeId })
          await this.$store.dispatch('setLocalStorageShopInfo')
          this.tabConfirm.isSubmitted = false
          this.progress.active = 3
        }
      }
    },
    async payWithPromptPay(form) {
      this.tabConfirm.isSubmitted = true

      const responseUpdatePaymentMethod = await this.updatePaymentMethod({
        type: form.paymentMethod,
        data: null,
      })

      if (!responseUpdatePaymentMethod.success) {
        this.notify(this.$vs, {
          text: this.$t('message.somethingwentwrong'),
          color: 'warning',
          icon: 'warning',
        })
        this.tabConfirm.isSubmitted = false
      } else {
        const responseSource = await axios.post(project.config.web.omise_core_api, {
          type: 'promptpay',
          amount: this.totalNumber * 100,
          currency: 'THB',
        }, {
          auth: {
            username: project.config.web.omise_public_key,
          },
        })

        const responseChargePackage = await this.chargePackage({
          'package': this.packageKey,
          'month': this.monthNumber,
          'detail': this.detail,
          'token': responseSource.data.id,
          'redirect_uri': `${project.config.web.app}desktop/payment/package/done`,
        })

        console.log('responseChargePackage', responseChargePackage)

        const chargeId = get(responseChargePackage, 'data.charge.id')
        const qrCodeUri = get(responseChargePackage, 'data.charge.qrcode_uri')

        this.qrCodeUri = qrCodeUri

        if (!responseChargePackage.success || !chargeId) {
          this.notify(this.$vs, {
            text: this.$t('apps.payment.message.paymentfailed'),
            color: 'warning',
            icon: 'warning',
          })
          this.tabConfirm.isSubmitted = false
        } else if (qrCodeUri) {
          this.tabConfirm.isSubmitted = false
          LocalStorage.set('chargeId', chargeId)
          $(`#${this.MODAL_SCAN_PAYMENT}`).modal('show')

          this.fetchChargeStatus()
          this.timer = setInterval(() => {
            if (!this.isScanPromptPayDone) {
              this.fetchChargeStatus()
            }
          }, 2000)
        }
      }
    },
    async onConfirmClickedNext (form) {
      try {
        switch (form.paymentMethod) {
          case BILLING_PAYMENT_METHOD.CARD:
            this.payWithCard(form)
            break
          case BILLING_PAYMENT_METHOD.PROMPTPAY:
            this.payWithPromptPay(form)
            break
        }
      } catch (e) {
        this.notify(this.$vs, {
          text: this.$t('message.somethingwentwrong'),
          color: 'warning',
          icon: 'warning',
        })
      }
    },
    onPaidClickedNext () {
      this.$router.push({ name: 'Home' })
    },
    async fetchChargeStatus () {
      const done = () => {
        clearInterval(this.timer)
        LocalStorage.remove('chargeId')
      }
      const chargeId = LocalStorage.get('chargeId')
      const response = await this.getTransactionBy({ chargeId })
      if (response.data.charge.status === 'successful' && response.data.transaction) {
        done()
        $(`#${this.MODAL_SCAN_PAYMENT}`).modal('hide')
        await this.$store.dispatch('setLocalStorageShopInfo')
        this.isScanPromptPayDone = true
        this.isPaymentFailed = false
        this.tabConfirm.isSubmitted = false
        this.progress.active = 3
      } else if (['failed'].includes(response.data.charge.status)) {
        done()
        $(`#${this.MODAL_SCAN_PAYMENT}`).modal('hide')
        this.isScanPromptPayDone = false
        this.isPaymentFailed = true
      }
    },
  },
}
</script>

<style scoped>
.main {
  margin-top: 50px;
}

.text-header {
  font-size: 22px;
}

.custom-step-progress {
  max-width: 600px;
  margin: 30px auto;
}
</style>



// WEBPACK FOOTER //
// src/components/paymentpackage/Invoice.vue